import type { i18n as I18nInstance } from 'i18next'
import type { ResolvedTerm, TermKey, TermLocale, TermOverride } from '../types'

/**
 * Terminology: what a tenant calls its hierarchy levels and goal types.
 *
 * Customers do not share our vocabulary — a "value stream" is a "product area"
 * somewhere else, a "business opportunity" is a "bet". Rather than route ~700
 * translated strings through per-call-site props, every locale string names a
 * concept with a placeholder (`{{valueStream}}`, `{{objectives, lower}}`) and
 * those placeholders are filled from i18next's `interpolation.defaultVariables`.
 * That means no `t()` call site has to change, and a rename reaches every
 * sentence in the product at once.
 *
 * The resolution rule mirrors `TerminologyService` on the backend exactly. Keep
 * them in step; a divergence shows up as the UI and an AI report calling the
 * same thing two different names.
 */

/** The built-in word for each concept, per language, before a tenant renames anything. */
export const TERM_DEFAULTS: Record<TermLocale, Record<TermKey, ResolvedTerm>> = {
  en: {
    ORGANIZATION: { singular: 'Organization', plural: 'Organizations', definiteSingular: 'Organization', definitePlural: 'Organizations' },
    DIVISION: { singular: 'Division', plural: 'Divisions', definiteSingular: 'Division', definitePlural: 'Divisions' },
    VALUE_STREAM: { singular: 'Value Stream', plural: 'Value Streams', definiteSingular: 'Value Stream', definitePlural: 'Value Streams' },
    PRODUCT_TEAM: { singular: 'Team', plural: 'Teams', definiteSingular: 'Team', definitePlural: 'Teams' },
    STRATEGIC_THEME: { singular: 'Strategic Theme', plural: 'Strategic Themes', definiteSingular: 'Strategic Theme', definitePlural: 'Strategic Themes' },
    STRATEGIC_INTENT: { singular: 'Strategic Intent', plural: 'Strategic Intents', definiteSingular: 'Strategic Intent', definitePlural: 'Strategic Intents' },
    BUSINESS_OPPORTUNITY: { singular: 'Business Opportunity', plural: 'Business Opportunities', definiteSingular: 'Business Opportunity', definitePlural: 'Business Opportunities' },
    OUTCOME_HYPOTHESIS: { singular: 'Outcome Hypothesis', plural: 'Outcome Hypotheses', definiteSingular: 'Outcome Hypothesis', definitePlural: 'Outcome Hypotheses' },
    // The goal type is "OKR"; a single one of them prints as "Objective".
    OKR: { singular: 'Objective', plural: 'Objectives', definiteSingular: 'Objective', definitePlural: 'Objectives' },
    KEY_RESULT: { singular: 'Key Result', plural: 'Key Results', definiteSingular: 'Key Result', definitePlural: 'Key Results' },
  },
  nb: {
    ORGANIZATION: {
      singular: 'Organisasjon', plural: 'Organisasjoner',
      definiteSingular: 'Organisasjonen', definitePlural: 'Organisasjonene',
    },
    DIVISION: {
      singular: 'Divisjon', plural: 'Divisjoner',
      definiteSingular: 'Divisjonen', definitePlural: 'Divisjonene',
    },
    VALUE_STREAM: {
      singular: 'Verdistrøm', plural: 'Verdistrømmer',
      definiteSingular: 'Verdistrømmen', definitePlural: 'Verdistrømmene',
    },
    // "team" and "mål" are neuter and take no plural ending.
    PRODUCT_TEAM: {
      singular: 'Team', plural: 'Team',
      definiteSingular: 'Teamet', definitePlural: 'Teamene',
    },
    // An adjective plus a noun also takes a leading article in the definite.
    STRATEGIC_THEME: {
      singular: 'Strategisk tema', plural: 'Strategiske temaer',
      definiteSingular: 'Det strategiske temaet', definitePlural: 'De strategiske temaene',
    },
    STRATEGIC_INTENT: {
      singular: 'Strategisk intensjon', plural: 'Strategiske intensjoner',
      definiteSingular: 'Den strategiske intensjonen', definitePlural: 'De strategiske intensjonene',
    },
    BUSINESS_OPPORTUNITY: {
      singular: 'Forretningsmulighet', plural: 'Forretningsmuligheter',
      definiteSingular: 'Forretningsmuligheten', definitePlural: 'Forretningsmulighetene',
    },
    OUTCOME_HYPOTHESIS: {
      singular: 'Utfallshypotese', plural: 'Utfallshypoteser',
      definiteSingular: 'Utfallshypotesen', definitePlural: 'Utfallshypotesene',
    },
    OKR: {
      singular: 'Mål', plural: 'Mål',
      definiteSingular: 'Målet', definitePlural: 'Målene',
    },
    KEY_RESULT: {
      singular: 'Nøkkelresultat', plural: 'Nøkkelresultater',
      definiteSingular: 'Nøkkelresultatet', definitePlural: 'Nøkkelresultatene',
    },
  },
}

export const TERM_KEYS = Object.keys(TERM_DEFAULTS.en) as TermKey[]

export const FALLBACK_LOCALE: TermLocale = 'en'

/**
 * The interpolation variable each concept fills. Singular and plural are
 * separate variables rather than an i18next plural form, because the sentence
 * usually knows which it wants ("Create {{objective}}" / "No {{objectives}}")
 * and a count is rarely in scope.
 *
 * `division`, `valueStream` and `team` match the names the dozen already
 * interpolated keys use, so those keep working untouched.
 */
export const TERM_VARIABLES: Record<
  TermKey,
  {
    singular: string
    plural: string
    definiteSingular: string
    definitePlural: string
    /** Indefinite article plus the term: "a bet", "et team". */
    withArticle: string
  }
> = {
  ORGANIZATION: { singular: 'organization', plural: 'organizations', definiteSingular: 'theOrganization', definitePlural: 'theOrganizations', withArticle: 'aOrganization' },
  DIVISION: { singular: 'division', plural: 'divisions', definiteSingular: 'theDivision', definitePlural: 'theDivisions', withArticle: 'aDivision' },
  VALUE_STREAM: { singular: 'valueStream', plural: 'valueStreams', definiteSingular: 'theValueStream', definitePlural: 'theValueStreams', withArticle: 'aValueStream' },
  PRODUCT_TEAM: { singular: 'team', plural: 'teams', definiteSingular: 'theTeam', definitePlural: 'theTeams', withArticle: 'aTeam' },
  STRATEGIC_THEME: { singular: 'theme', plural: 'themes', definiteSingular: 'theTheme', definitePlural: 'theThemes', withArticle: 'aTheme' },
  STRATEGIC_INTENT: { singular: 'intent', plural: 'intents', definiteSingular: 'theIntent', definitePlural: 'theIntents', withArticle: 'aIntent' },
  BUSINESS_OPPORTUNITY: { singular: 'opportunity', plural: 'opportunities', definiteSingular: 'theOpportunity', definitePlural: 'theOpportunities', withArticle: 'aOpportunity' },
  OUTCOME_HYPOTHESIS: { singular: 'hypothesis', plural: 'hypotheses', definiteSingular: 'theHypothesis', definitePlural: 'theHypotheses', withArticle: 'aHypothesis' },
  OKR: { singular: 'objective', plural: 'objectives', definiteSingular: 'theObjective', definitePlural: 'theObjectives', withArticle: 'aObjective' },
  KEY_RESULT: { singular: 'keyResult', plural: 'keyResults', definiteSingular: 'theKeyResult', definitePlural: 'theKeyResults', withArticle: 'aKeyResult' },
}

/**
 * The indefinite article for a term, in the tenant's own vocabulary.
 *
 * English picks a/an from the first sound. Norwegian picks en/et from
 * grammatical gender, which nobody types in — but the definite form encodes it:
 * a neuter noun takes "-et" ("teamet" → "et team"), everything else "-en"
 * ("verdistrømmen" → "en verdistrøm"). A tenant that skips the definite form
 * falls back to "en", which is right for the large majority of Norwegian nouns.
 */
export function indefiniteArticle(locale: TermLocale, term: ResolvedTerm): string {
  if (locale === 'nb') {
    const definite = term.definiteSingular?.trim().toLowerCase() ?? ''
    const isNeuter = definite.endsWith('et') && !definite.endsWith('heten')
    return isNeuter ? 'et' : 'en'
  }
  return /^[aeiou]/i.test(term.singular.trim()) ? 'an' : 'a'
}

/** Accepts what a browser or preference actually sends: `nb-NO`, `EN`, `en-GB`. */
export function normalizeLocale(locale: string | null | undefined): TermLocale {
  const base = locale?.trim().toLowerCase().split('-')[0]
  return base === 'nb' ? 'nb' : FALLBACK_LOCALE
}

/**
 * Resolve one tenant's vocabulary for one language.
 *
 * In order: the tenant's term in the requested language, then the tenant's
 * English term, then our built-in term in the requested language.
 *
 * The middle step is the product decision. A company that renamed "Value
 * Stream" to "Product Area" in English only should see "Product Area" in the
 * Norwegian UI too — showing them "Verdistrøm" there would be showing them a
 * word nobody in that company uses. A tenant that renamed nothing keeps the
 * fully translated defaults, so this never degrades an untouched workspace.
 */
export function resolveTerms(
  overrides: TermOverride[],
  locale: string | null | undefined,
): Record<TermKey, ResolvedTerm> {
  const requested = normalizeLocale(locale)
  const byKeyAndLocale = new Map<string, TermOverride>()
  overrides.forEach((o) => byKeyAndLocale.set(`${o.termKey}:${o.locale}`, o))

  const resolved = {} as Record<TermKey, ResolvedTerm>
  TERM_KEYS.forEach((key) => {
    const own = byKeyAndLocale.get(`${key}:${requested}`)
    const english = byKeyAndLocale.get(`${key}:${FALLBACK_LOCALE}`)
    // The whole term is chosen first, then its forms are read off it. Mixing
    // forms across sources would let a tenant's English name carry our
    // Norwegian definite suffix.
    const chosen = own ?? english
    const fallback = TERM_DEFAULTS[requested][key]
    resolved[key] = chosen
      ? {
          singular: chosen.singular,
          plural: chosen.plural,
          // A tenant that renamed but skipped the definite gets the indefinite
          // form: terse, never wrong-looking.
          definiteSingular: chosen.definiteSingular?.trim() || chosen.singular,
          definitePlural: chosen.definitePlural?.trim() || chosen.plural,
        }
      : fallback
  })
  return resolved
}

/**
 * Flatten a resolved vocabulary into the `{{variable}}` map i18next
 * interpolates with. Values keep the capitalisation the tenant typed; a
 * sentence that needs lowercase asks for it with `{{valueStream, lower}}`.
 */
export function toInterpolationVariables(
  terms: Record<TermKey, ResolvedTerm>,
  locale: TermLocale = FALLBACK_LOCALE,
): Record<string, string> {
  const vars: Record<string, string> = {}
  TERM_KEYS.forEach((key) => {
    const names = TERM_VARIABLES[key]
    vars[names.singular] = terms[key].singular
    vars[names.plural] = terms[key].plural
    vars[names.definiteSingular] = terms[key].definiteSingular
    vars[names.definitePlural] = terms[key].definitePlural
    vars[names.withArticle] = `${indefiniteArticle(locale, terms[key])} ${terms[key].singular}`
  })
  return vars
}

/**
 * Point an i18next instance's interpolation at a tenant's vocabulary.
 *
 * Takes the instance rather than reaching for the bootstrap singleton, so a
 * component that only needs terminology does not pull in i18n initialisation
 * (and so tests can drive it with their own instance).
 */
export function setTerminologyVariables(
  i18n: Pick<I18nInstance, 'options'> | undefined | null,
  variables: Record<string, string>,
) {
  // An instance that has not finished initialising has no options yet. Wording
  // is never worth taking a screen down for, so this is a no-op rather than a
  // crash -- the built-in words are already correct until it settles.
  if (!i18n?.options) return
  i18n.options.interpolation = {
    ...i18n.options.interpolation,
    defaultVariables: variables,
  }
}
